import { tableauVizService, type VizDataRecord } from "@vm/tableau";

import { logger } from "@/logger";

import {
  type ComparisonDataService,
  type ComparisonObject,
  RawComparisonObjectSchema,
} from "./comparison-data-service";

function parseRecordToComparisonObject(
  record: VizDataRecord,
): ComparisonObject | undefined {
  try {
    return RawComparisonObjectSchema.parse(record);
  } catch (error) {
    logger.error("Failed to parse record", error);
    return undefined;
  }
}

export class ComparisonDataServiceTableau implements ComparisonDataService {
  private vizDataCallback: ((objects: ComparisonObject[]) => void) | undefined;
  private clearSummaryListener: (() => void) | undefined;

  async initialize(): Promise<void> {
    await this.loadVizData();

    this.clearSummaryListener = tableauVizService.addEventListener(
      tableau.TableauEventType.SummaryDataChanged,
      () => this.loadVizData(),
    ).clear;
  }

  onVizDataLoaded(callback: (objects: ComparisonObject[]) => void): () => void {
    this.vizDataCallback = callback;
    return () => {
      this.vizDataCallback = undefined;
      this.clearSummaryListener?.();
    };
  }

  private async loadVizData(): Promise<void> {
    try {
      const result = await tableauVizService.loadSummaryVizData();
      const objects = result.data
        .map((record) => parseRecordToComparisonObject(record))
        .filter((obj): obj is ComparisonObject => obj !== undefined);
      this.vizDataCallback?.(objects);
    } catch (error) {
      logger.error("Failed to load viz data", error);
    }
  }
}
